/**
 * 認証フック
 *
 * @file useAuth.ts
 * @version 1.0.0
 * @author CrossBorderECMall Development Team
 * @summary
 * AuthContextType 型定義、AuthContext オブジェクト、認証・ロール判定フックを提供。
 * AuthContext.tsx から分離し Fast Refresh 互換を確保。
 */
import { createContext, useContext } from 'react';
import { AdminLoginOtpRequiredResponse, SellerLoginOtpRequiredResponse, User, AdminUser, UserRole, RegisterRequest } from '@/types';

export interface AuthContextType {
  user: User | AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string, role: 'buyer' | 'seller' | 'admin', remember?: boolean) => Promise<AdminLoginOtpRequiredResponse | void>;
  loginBuyer: (email: string, password: string, remember?: boolean) => Promise<void>;
  loginSeller: (email: string, password: string, remember?: boolean) => Promise<SellerLoginOtpRequiredResponse | void>;
  loginAdmin: (email: string, password: string) => Promise<AdminLoginOtpRequiredResponse | void>;
  verifyAdminOtp: (email: string, password: string, otpCode: string) => Promise<void>;
  verifySellerOtp: (email: string, password: string, otpCode: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  setForcePasswordChangeFalse: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useIsAdmin = (): boolean => {
  const { user } = useAuth();
  return user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.EDITOR || user?.role === UserRole.VIEWER;
};

export const useIsSeller = (): boolean => {
  const { user } = useAuth();
  return user?.role === UserRole.SELLER;
};

export const useIsCustomer = (): boolean => {
  const { user } = useAuth();
  return user?.role === UserRole.BUYER;
};

export const useAdminRole = (): UserRole | null => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) {
    return null;
  }
  return user.role as UserRole;
};

export const useHasPermission = (code: string): boolean => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) {
    return false;
  }
  const adminUser = user as import('@/types').AdminUser;
  if (!Array.isArray(adminUser.permissions)) {
    return false;
  }
  return adminUser.permissions.includes(code);
};
